import React, { useState } from 'react';
import { AssessmentResult } from '../types/assessment';

interface ResultsViewProps {
  assessmentResult: AssessmentResult | null;
  resultUrl?: string | null;
  showFollowUp?: boolean;
}

const bookingLinks = {
  ani: process.env.REACT_APP_BOOKING_ANI_URL || '',
  maky: process.env.REACT_APP_BOOKING_MAKY_URL || '',
};

const getStepColor = (index: number) => {
  switch (index) {
    case 0:
      return '#10B981';
    case 1:
      return '#3B82F6';
    case 2:
      return '#8B5CF6';
    default:
      return '#6B7280';
  }
};

const stepTitles = ['Krok 1', 'Krok 2', 'Krok 3'];

const getTierLabel = (tier?: string) => {
  switch (tier) {
    case 'low_friction':
      return 'Nízká časová náročnost';
    case 'core_product':
      return 'Strukturovaná podpora';
    case 'high_touch':
      return 'Vedená podpora';
    case 'premium':
      return 'Prémiová podpora';
    default:
      return 'Doporučeno';
  }
};

const FollowUpSection: React.FC = () => (
  <section className="result-section result-section-tip" style={{ marginTop: '1.5rem' }}>
    <h3 style={{ marginTop: 0 }}>Závěr &amp; follow-up</h3>
    <p className="result-note" style={{ marginBottom: '0.75rem' }}>
      Děkujeme! Na základě tvých odpovědí ti do <strong>2-3 pracovních dnů</strong> zašleme osobní vyhodnocení.
    </p>
    <p className="result-strong" style={{ marginBottom: '1rem' }}>
      Díky vyplnění celého dotazníku jsi právě odemknul/a svůj bonus!
    </p>
    <p style={{ marginTop: 0 }}>
      Už teď se můžeš objednat na 1:1 koučovací schůzku s Ani nebo Maky.
    </p>

    <div style={{ display: 'grid', gap: '0.75rem' }}>
      <div className="result-item">
        <h4 style={{ margin: '0 0 0.5rem 0' }}>Ani</h4>
        <p style={{ margin: '0 0 0.75rem 0' }}>
          Koučka zaměřená na změnu směru, osobní stabilitu a praktické kroky v přechodových obdobích.
        </p>
        {bookingLinks.ani ? (
          <a href={bookingLinks.ani} target="_blank" rel="noreferrer">
            Rezervovat termín s Ani
          </a>
        ) : (
          <span className="result-note">Odkaz na booking s Ani doplníme.</span>
        )}
      </div>

      <div className="result-item">
        <h4 style={{ margin: '0 0 0.5rem 0' }}>Maky</h4>
        <p style={{ margin: '0 0 0.75rem 0' }}>
          Koučka zaměřená na sebevědomí, práci s tlakem a dlouhodobě udržitelný rozvoj.
        </p>
        {bookingLinks.maky ? (
          <a href={bookingLinks.maky} target="_blank" rel="noreferrer">
            Rezervovat termín s Maky
          </a>
        ) : (
          <span className="result-note">Odkaz na booking s Maky doplníme.</span>
        )}
      </div>
    </div>
  </section>
);

const CopyLinkSection: React.FC<{ resultUrl: string }> = ({ resultUrl }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(resultUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      window.prompt('Zkopírujte odkaz k vašim výsledkům:', resultUrl);
    }
  };

  return (
    <section className="result-section" style={{ backgroundColor: '#EEF2FF', marginBottom: '1.5rem' }}>
      <h3 style={{ marginTop: 0 }}>Tvůj trvalý odkaz k výsledkům</h3>
      <p className="result-note" style={{ marginBottom: '1rem' }}>
        Ulož si tento odkaz — můžeš se k vyhodnocení vrátit kdykoli po dobu 2 let.
      </p>
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <code
          style={{
            flex: '1 1 200px',
            padding: '0.75rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            wordBreak: 'break-all',
            fontSize: '0.85rem',
          }}
        >
          {resultUrl}
        </code>
        <button type="button" onClick={handleCopy} className="question-btn question-btn-primary">
          {copied ? 'Zkopírováno' : 'Zkopírovat odkaz'}
        </button>
      </div>
    </section>
  );
};

export const ResultsView: React.FC<ResultsViewProps> = ({
  assessmentResult,
  resultUrl,
  showFollowUp = true,
}) => {
  return (
    <div className="result-page">
      <div className="result-card">
        <h2 className="result-title">Tvoje vyhodnocení je připravené</h2>

        {resultUrl && <CopyLinkSection resultUrl={resultUrl} />}

        {assessmentResult ? (
          <div>
            <section className="result-section result-section-primary">
              <h3>Shrnutí</h3>
              <p className="result-strong" style={{ marginTop: 0 }}>
                {assessmentResult.summary.headline}
              </p>
              <p style={{ marginBottom: '1rem' }}>{assessmentResult.summary.interpretation}</p>
              <ul className="result-list">
                {assessmentResult.summary.bullets.map((bullet, index) => (
                  <li key={index}>{bullet}</li>
                ))}
              </ul>
            </section>

            <section className="result-section result-section-highlight">
              <h3>Co o tobě vychází</h3>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div className="result-item">
                  <strong>Hlavní fokus:</strong> {assessmentResult.evaluation.focus_area_label}
                </div>
                <div className="result-item">
                  <strong>Největší brzda:</strong> {assessmentResult.evaluation.blocker_label}
                </div>
                <div className="result-item">
                  <strong>Doporučený styl podpory:</strong>{' '}
                  {assessmentResult.evaluation.recommended_support_label}
                </div>
                <div className="result-item">
                  <strong>Tempo:</strong> {assessmentResult.evaluation.pace_label}
                </div>
              </div>
            </section>

            <section className="result-section" style={{ marginTop: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', textAlign: 'center' }}>
                Tvoje doporučení na míru
              </h3>

              {assessmentResult.recommendations.length > 0 ? (
                assessmentResult.recommendations.map((rec, index) => (
                  <div
                    key={rec.product_id}
                    className="result-item"
                    style={{
                      borderLeft: `5px solid ${getStepColor(index)}`,
                      marginBottom: '1.5rem',
                    }}
                  >
                    <h4 style={{ color: getStepColor(index), marginBottom: '0.5rem' }}>
                      {rec.role_label || stepTitles[index] || `Krok ${index + 1}`}
                    </h4>
                    <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                      {rec.personalized_message ||
                        'Doporučení vybrané podle tvého tématu, kapacity a preferované podpory.'}
                    </p>
                    <h5 style={{ fontSize: '1.2rem', margin: '0.5rem 0' }}>{rec.product_name}</h5>
                    <p style={{ fontStyle: 'italic', marginBottom: '1rem' }}>"{rec.reasoning}"</p>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        className="result-badge"
                        style={{ backgroundColor: getStepColor(index), color: 'white' }}
                      >
                        {getTierLabel(rec.tier)}
                      </span>
                      {rec.url ? (
                        <a
                          href={rec.url}
                          target="_blank"
                          rel="noreferrer"
                          className="question-btn-primary"
                          style={{ padding: '0.5rem 1rem', fontSize: '0.9rem', textDecoration: 'none' }}
                        >
                          Zjistit více
                        </a>
                      ) : (
                        <span className="result-note">Odkaz doplníme</span>
                      )}
                    </div>
                    {typeof rec.product_price === 'number' && rec.product_price > 0 && (
                      <p className="result-note" style={{ marginTop: '0.75rem' }}>
                        Orientační cena: {rec.product_price} Kč
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <p className="result-note" style={{ textAlign: 'center' }}>
                  Konkrétní doporučení se nepodařilo načíst. Zůstaň u dalšího doporučeného kroku níže.
                </p>
              )}
            </section>

            <section className="result-section" style={{ backgroundColor: '#F3F4F6' }}>
              <h4>Další doporučený krok</h4>
              <p className="result-quote">
                "
                {assessmentResult.next_step ||
                  'Začni jedním realistickým krokem, který zvládneš udržet.'}
                "
              </p>
              {assessmentResult.lead_capture?.email && (
                <p className="result-note" style={{ marginTop: '1rem' }}>
                  Potvrzení jsme spárovali s kontaktem:{' '}
                  <strong>{assessmentResult.lead_capture.email}</strong>
                </p>
              )}
            </section>

            {showFollowUp && <FollowUpSection />}
          </div>
        ) : (
          <div className="result-empty">
            <p>Výsledky se nepodařilo načíst. Zkuste odkaz znovu nebo kontaktujte podporu.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsView;
